// shared/projects.ts
// Projects, render jobs and the worker's status — the vocabulary shared by
// api/src/projects.ts + jobs.ts (validation, the source of truth) and the
// Projects/Jobs pages. A project's spec is a native make_video spec (see
// FbAdPipeline/docs/make-video.md) except that file fields hold
// `asset:<assetId>` references into the owner's library (or `builtin:<id>`
// for the shared images in BUILTIN_IMAGES) instead of paths;
// ring/worker/resolve.py turns those into files at render time.
// ─────────────────────────────────────────────────────────────────
import { isAssetId } from './assets.js';
export const PROJECT_STATUSES = ['draft', 'ready'];
export function isProjectStatus(v) {
    return typeof v === 'string' && PROJECT_STATUSES.includes(v);
}
/** Who made the project: the user, or Claude drafting from hooks (phase 5). */
export const PROJECT_ORIGINS = ['manual', 'claude'];
export const JOB_TYPES = ['render', 'describe-assets', 'draft-from-hooks'];
/**
 * queued → running → done | failed. A job the worker gives up retrying is
 * failed; a worker stopped mid-job puts it back to queued. queued →
 * cancelled when the owner cancels it before the worker starts it (both
 * sides write conditionally on the row's ETag, so exactly one of cancel and
 * start wins).
 */
export const JOB_STATUSES = ['queued', 'running', 'done', 'failed', 'cancelled'];
export const MAX_PROJECT_NAME = 100;
/**
 * The spec is stored as one Table Storage string property, which holds at
 * most 32K UTF-16 characters — JSON.stringify's length counts the same
 * units. Leaves a little headroom. Plenty for a 180-second ad.
 */
export const MAX_SPEC_CHARS = 30_000;
/** Queued + running jobs one account may have at once, so one account can't fill the queue. */
export const MAX_ACTIVE_JOBS = 5;
/** The worker heartbeats every 30 seconds; this long without one reads as offline. */
export const WORKER_OFFLINE_AFTER_MS = 2 * 60 * 1000;
export const ASSET_REF_PREFIX = 'asset:';
/** The assetId in an `asset:<id>` reference, or null if `v` isn't one. */
export function parseAssetRef(v) {
    if (typeof v !== 'string' || !v.startsWith(ASSET_REF_PREFIX))
        return null;
    const id = v.slice(ASSET_REF_PREFIX.length);
    return isAssetId(id) ? id : null;
}
export const BUILTIN_REF_PREFIX = 'builtin:';
/**
 * Images every account can use without uploading them, referenced as
 * `builtin:<id>`. Served by the site from public/builtin/<file> (for the
 * preview) and read by the worker from the same folder. A built-in is an
 * image: it's accepted in any file field that takes one.
 */
export const BUILTIN_IMAGES = {
    'ku-black': { label: 'Kindle Unlimited (black)', file: 'ku-black.jpg' },
    'ku-white': { label: 'Kindle Unlimited (white)', file: 'ku-white.jpg' },
};
/** The id in a `builtin:<id>` reference, or null if `v` isn't one. */
export function parseBuiltinRef(v) {
    if (typeof v !== 'string' || !v.startsWith(BUILTIN_REF_PREFIX))
        return null;
    const id = v.slice(BUILTIN_REF_PREFIX.length);
    return Object.hasOwn(BUILTIN_IMAGES, id) ? id : null;
}
function asObject(v) {
    return v !== null && typeof v === 'object' && !Array.isArray(v) ? v : null;
}
/**
 * Every field of a make_video spec that names a file. Must stay in step
 * with FILE_SLOTS in ring/worker/resolve.py: a file field missing from both
 * would reach make_video unresolved (where `root` confinement still rejects
 * it, but with a confusing message).
 */
export function specFileSlots(spec) {
    const slots = [];
    const add = (owner, key, label, kinds) => {
        if (owner && key in owner)
            slots.push({ label, value: owner[key], kinds });
    };
    const background = asObject(spec.background);
    add(background, 'file', 'background.file', ['image', 'video']);
    if (Array.isArray(background?.overlays)) {
        background.overlays.forEach((o, j) => add(asObject(o), 'file', `background.overlays[${j}].file`, ['image']));
    }
    add(asObject(spec.audio), 'file', 'audio.file', ['music']);
    add(asObject(spec.endCard), 'image', 'endCard.image', ['image']);
    if (Array.isArray(spec.beats)) {
        spec.beats.forEach((beat, i) => {
            const images = asObject(beat)?.images;
            if (!Array.isArray(images))
                return;
            images.forEach((im, j) => add(asObject(im), 'file', `beats[${i}].images[${j}].file`, ['image']));
        });
    }
    return slots;
}
/** What a new project starts with: a 9:16 video with one beat and no background picked yet. */
export const STARTER_SPEC = {
    size: [1080, 1920],
    background: { file: '', zoom: { target: 'center', from: 1.0, to: 1.25 } },
    audio: { file: '' },
    style: { template: 'default' },
    beats: [{ text: 'Your opening line.', duration: 2.5 }],
};
